import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Chapter from "./Chapter";
import { IDS } from "@/lib/ids";

const BLOCKS = [
  { id: "#531", entity: "user",  action: "LOGGED_IN", summary: "Admin — signed in with password", hash: "d63d7af526ad" },
  { id: "#532", entity: "risk",  action: "UPDATED",   summary: "Key vendor data breach — score 20 → 14", hash: "cbab0162d425" },
  { id: "#533", entity: "evi",   action: "CREATED",   summary: "access-review-2026-Q3.pdf attached to Access Review", hash: "37b541eda800" },
  { id: "#534", entity: "ctrl",  action: "SATISFIED", summary: "A.5.23 · CC6.1 · Art.32 · §164.312(a) — auto-satisfied", hash: "8012b9e69683" },
  { id: "#535", entity: "policy",action: "PUBLISHED", summary: "Encryption Policy v3.2 — acknowledged by 24 employees", hash: "057dcfc49e51" },
];

function scramble(s) {
  const chars = "abcdef0123456789";
  return s
    .split("")
    .map((c) => (Math.random() < 0.7 ? chars[Math.floor(Math.random() * chars.length)] : c))
    .join("");
}

export default function HashChain() {
  const [tampered, setTampered] = useState(false);

  return (
    <section id="trust" className="relative py-14 md:py-20 hairline-t">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Chapter number="08" label="Trust · by mechanism" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
              className="font-display text-[36px] md:text-[52px] leading-[1.04] tracking-[-0.022em] text-balance"
            >
              No testimonial wall. Just a <span className="grad-text">verifiable</span> audit trail.
            </motion.h2>
            <p className="mt-5 text-ink-muted text-[15px] leading-relaxed max-w-[46ch]">
              Every change to a control, evidence, risk or policy is written to a hash-chained activity log. An auditor can walk the chain, block by block, and prove it hasn't been rewritten. Try tampering.
            </p>
          </div>

          <div className="lg:col-span-7">
            <div className="machined p-6 md:p-8 relative">
              <div className="flex items-center justify-between mb-6">
                <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-ink-muted">Activity log · hash chain</div>
                <button
                  data-testid={IDS.hashTamperToggle}
                  onClick={() => setTampered((t) => !t)}
                  className={`px-3 py-1.5 rounded-full border font-mono text-[10px] tracking-[0.18em] uppercase transition-[background,color,border-color] duration-300 ${tampered ? "border-brand-red bg-brand-red/10 text-brand-red" : "border-white/12 text-white/60 hover:text-white/85"}`}
                >
                  {tampered ? "Tamper: on · chain broken" : "Simulate tampering"}
                </button>
              </div>

              <div className="space-y-3">
                {BLOCKS.map((b, i) => {
                  const isBroken = tampered && i === 2;
                  const isAfterBreak = tampered && i > 2;
                  return (
                    <div key={b.id} className="relative">
                      {i > 0 && (
                        <div className={`absolute -top-3 left-8 h-3 w-px transition-colors duration-300 ${tampered && i === 3 ? "bg-brand-red animate-pulse" : "bg-white/15"}`} />
                      )}
                      <motion.div
                        initial={false}
                        animate={{
                          borderColor: isBroken ? "rgba(232,37,50,0.5)" : (isAfterBreak ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.10)"),
                          backgroundColor: isBroken ? "rgba(232,37,50,0.08)" : "rgba(255,255,255,0.02)",
                        }}
                        className={`rounded-lg border px-4 py-3 flex items-center gap-3 relative`}
                      >
                        <span className="font-mono text-[10px] tracking-[0.18em] text-white/40 tabular">{b.id}</span>
                        <span className={`px-2 py-0.5 rounded-sm border font-mono text-[9px] tracking-[0.16em] uppercase ${b.action === "SATISFIED" ? "border-emerald-400/40 text-emerald-300" : b.action === "CREATED" ? "border-emerald-400/30 text-emerald-300/90" : b.action === "UPDATED" ? "border-blue-400/30 text-blue-300" : b.action === "PUBLISHED" ? "border-indigo-400/30 text-indigo-300" : "border-white/15 text-white/60"}`}>
                          {b.action}
                        </span>
                        <span className={`flex-1 text-[13px] ${isAfterBreak ? "text-white/35 line-through" : "text-white/85"}`}>
                          {b.summary}
                        </span>
                        <motion.span
                          className={`font-mono text-[10px] tabular tracking-[0.06em] ${isBroken ? "text-brand-red" : "text-white/45"}`}
                          animate={isBroken ? { x: [0, -1, 1, -1, 0] } : {}}
                          transition={{ duration: 0.15, repeat: isBroken ? Infinity : 0 }}
                        >
                          {isBroken ? scramble(b.hash) : b.hash}
                        </motion.span>
                      </motion.div>
                    </div>
                  );
                })}
              </div>

              <AnimatePresence>
                {tampered && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-5 px-4 py-3 rounded-md border border-brand-red/40 bg-brand-red/[0.05] font-mono text-[11px] text-brand-red tabular"
                  >
                    chain::verify() → FAILED at #533 · next-hash mismatch · downstream blocks invalidated
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
